// /admin/audit — filterable audit log browser.
//
// Filters: user, entity, entity_id, action prefix, date range.
// Pagination via limit/offset.
// Detail view: before_json / after_json side-by-side with line-level diff.
// Admin-gated on API; this page treats 401/403 the same as other admin
// pages and redirects to login or surfaces a "needs admin role" notice.

import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';

interface AuditRow {
  id: string;
  user_id: string | null;
  user_email: string | null;
  user_display_name: string | null;
  action: string;
  entity: string;
  entity_id: string;
  occurred_at: number;
  context: string | null;
}

interface AuditDetail extends AuditRow {
  before_json: string | null;
  after_json: string | null;
}

interface AuditFacets {
  entities: string[];
  actions: string[];
  users: Array<{ id: string; email: string; display_name: string }>;
}

const PAGE_SIZE = 50;

export default function AdminAuditPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    user_id: '',
    entity: '',
    entity_id: '',
    action_prefix: '',
    since: '',
    until: '',
  });
  const [offset, setOffset] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const facets = useQuery({
    queryKey: ['admin', 'audit', 'facets'],
    queryFn: () => api.get<AuditFacets>('/admin/audit-facets'),
    staleTime: 60_000,
  });

  const qs = useMemo(() => {
    const params = new URLSearchParams();
    if (filters.user_id) params.set('user_id', filters.user_id);
    if (filters.entity) params.set('entity', filters.entity);
    if (filters.entity_id) params.set('entity_id', filters.entity_id);
    if (filters.action_prefix) params.set('action_prefix', filters.action_prefix);
    const sinceMs = dateInputToMs(filters.since, 'start');
    if (sinceMs != null) params.set('since', String(sinceMs));
    const untilMs = dateInputToMs(filters.until, 'end');
    if (untilMs != null) params.set('until', String(untilMs));
    params.set('limit', String(PAGE_SIZE));
    params.set('offset', String(offset));
    return params.toString();
  }, [filters, offset]);

  const list = useQuery({
    queryKey: ['admin', 'audit', qs],
    queryFn: () => api.get<AuditRow[]>(`/admin/audit?${qs}`),
  });

  const detail = useQuery({
    queryKey: ['admin', 'audit', 'detail', selectedId],
    queryFn: () => api.get<AuditDetail>(`/admin/audit/${selectedId}`),
    enabled: selectedId != null,
  });

  if (list.error instanceof ApiError) {
    if (list.error.status === 401) { navigate({ to: '/login' }); return null; }
    if (list.error.status === 403) return <Padded>This page requires the admin role.</Padded>;
  }

  const rows = list.data?.data ?? [];
  const total = (list.data?.meta as { total?: number } | undefined)?.total ?? 0;

  const updateFilter = <K extends keyof typeof filters>(k: K, v: string) => {
    setFilters((prev) => ({ ...prev, [k]: v }));
    setOffset(0);
  };
  const clearFilters = () => {
    setFilters({ user_id: '', entity: '', entity_id: '', action_prefix: '', since: '', until: '' });
    setOffset(0);
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="max-w-[1400px] mx-auto px-5 py-5">
        <header className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-base font-semibold text-neutral-900">Audit log</h1>
            <p className="mt-1 text-xs text-neutral-600">
              Immutable record of every mutation. Use the filters to narrow by user, entity, action, or date range.
            </p>
          </div>
          <a
            href="/api/v1/admin/data-export"
            className="text-xs rounded-md border border-neutral-300 px-2 py-1 hover:bg-neutral-50 shrink-0"
            aria-label="Download a ZIP archive of all database tables and raw source files"
          >Download data export (.zip)</a>
        </header>

        <fieldset className="border border-neutral-200 rounded-md p-3 mb-4">
          <legend className="px-2 text-xs uppercase tracking-wide text-neutral-600">Filters</legend>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <FilterSelect
              label="User"
              value={filters.user_id}
              onChange={(v) => updateFilter('user_id', v)}
              options={(facets.data?.data.users ?? []).map((u) => ({ value: u.id, label: u.display_name || u.email }))}
            />
            <FilterSelect
              label="Entity"
              value={filters.entity}
              onChange={(v) => updateFilter('entity', v)}
              options={(facets.data?.data.entities ?? []).map((e) => ({ value: e, label: e }))}
            />
            <FilterText
              label="Entity ID"
              value={filters.entity_id}
              onChange={(v) => updateFilter('entity_id', v)}
              placeholder="exact match"
            />
            <FilterText
              label="Action prefix"
              value={filters.action_prefix}
              onChange={(v) => updateFilter('action_prefix', v)}
              placeholder="e.g. match:"
            />
            <FilterText
              label="From"
              value={filters.since}
              onChange={(v) => updateFilter('since', v)}
              type="date"
            />
            <FilterText
              label="To"
              value={filters.until}
              onChange={(v) => updateFilter('until', v)}
              type="date"
            />
          </div>
          <div className="mt-3 flex items-center gap-3 text-xs">
            <button
              type="button"
              onClick={clearFilters}
              className="rounded border border-neutral-300 px-2 py-1 hover:bg-neutral-50"
            >Clear filters</button>
            <span className="text-neutral-600">{total.toLocaleString()} matching entries</span>
          </div>
        </fieldset>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <section aria-label="Audit list" className="border border-neutral-200 rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wide text-neutral-600">
                <tr>
                  <th scope="col" className="px-3 py-2">When</th>
                  <th scope="col" className="px-3 py-2">Action</th>
                  <th scope="col" className="px-3 py-2">Entity</th>
                  <th scope="col" className="px-3 py-2">User</th>
                </tr>
              </thead>
              <tbody>
                {list.isLoading && (
                  <tr><td colSpan={4} className="px-3 py-4 text-center text-neutral-600">Loading audit log — a moment.</td></tr>
                )}
                {!list.isLoading && rows.length === 0 && (
                  <tr><td colSpan={4} className="px-3 py-4 text-center text-neutral-500">No matching entries.</td></tr>
                )}
                {rows.map((r) => {
                  const isSelected = selectedId === r.id;
                  return (
                    <tr
                      key={r.id}
                      className={
                        'border-t border-neutral-100 cursor-pointer hover:bg-neutral-50 ' +
                        (isSelected ? 'bg-neutral-100' : '')
                      }
                      onClick={() => setSelectedId(r.id)}
                    >
                      <td className="px-3 py-2 text-xs whitespace-nowrap">
                        <button
                          type="button"
                          className="text-left underline-offset-2 hover:underline focus:underline"
                          aria-label={`Open audit entry ${r.id}`}
                          onClick={(e) => { e.stopPropagation(); setSelectedId(r.id); }}
                        >
                          {formatTimestamp(r.occurred_at)}
                        </button>
                      </td>
                      <td className="px-3 py-2 text-xs font-mono">{r.action}</td>
                      <td className="px-3 py-2 text-xs">
                        <div>{r.entity}</div>
                        <div className="text-neutral-500 font-mono break-all">{r.entity_id}</div>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {r.user_display_name || r.user_email || <span className="text-neutral-500">system</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <nav className="flex items-center justify-between px-3 py-2 border-t border-neutral-200 bg-neutral-50 text-xs">
              <button
                type="button"
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                disabled={offset === 0}
                className="rounded border border-neutral-300 px-2 py-1 disabled:opacity-50 hover:bg-white"
              >Previous</button>
              <span className="text-neutral-600">
                {total === 0 ? '0' : `${offset + 1}–${Math.min(offset + rows.length, total)}`} of {total.toLocaleString()}
              </span>
              <button
                type="button"
                onClick={() => setOffset(offset + PAGE_SIZE)}
                disabled={offset + rows.length >= total}
                className="rounded border border-neutral-300 px-2 py-1 disabled:opacity-50 hover:bg-white"
              >Next</button>
            </nav>
          </section>

          <section aria-label="Audit detail" className="border border-neutral-200 rounded-md p-3">
            {!selectedId && (
              <p className="text-sm text-neutral-500">Select an entry to see the before/after diff.</p>
            )}
            {selectedId && detail.isLoading && <p className="text-sm text-neutral-600">Loading entry detail — a moment.</p>}
            {selectedId && detail.data && <AuditDetailView row={detail.data.data} />}
          </section>
        </div>
      </div>
    </div>
  );
}

function AuditDetailView({ row }: { row: AuditDetail }) {
  const before = safeParse(row.before_json);
  const after  = safeParse(row.after_json);
  const lines  = useMemo(() => diffJson(before, after), [row.id]);

  return (
    <div className="space-y-3 text-sm">
      <header className="border-b border-neutral-200 pb-2">
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-600">
          <div><span className="text-neutral-500">Action:</span> <span className="font-mono text-neutral-900">{row.action}</span></div>
          <div><span className="text-neutral-500">Entity:</span> <span className="text-neutral-900">{row.entity}</span></div>
          <div><span className="text-neutral-500">When:</span> {formatTimestamp(row.occurred_at)}</div>
          <div><span className="text-neutral-500">User:</span> {row.user_display_name || row.user_email || 'system'}</div>
        </div>
        <div className="mt-1 text-xs">
          <span className="text-neutral-500">Entity ID:</span> <span className="font-mono break-all">{row.entity_id}</span>
        </div>
      </header>

      <div>
        <h2 className="text-xs uppercase tracking-wide text-neutral-600 mb-1">Before / After</h2>
        {before == null && after == null && (
          <p className="text-xs text-neutral-500">No payload recorded for this entry (mutation had no before/after).</p>
        )}
        {(before != null || after != null) && (
          <pre className="text-xs font-mono bg-neutral-50 border border-neutral-200 rounded p-2 overflow-x-auto whitespace-pre">
            {lines.map((l, i) => (
              <div
                key={i}
                className={
                  l.kind === 'add'    ? 'bg-emerald-100 text-emerald-900' :
                  l.kind === 'remove' ? 'bg-rose-100 text-rose-900' :
                  l.kind === 'change' ? 'bg-amber-100 text-amber-900' :
                                        'text-neutral-700'
                }
              ><span aria-hidden="true" className="inline-block w-4">{l.marker}</span>{l.text}</div>
            ))}
          </pre>
        )}
      </div>

      {row.context && (
        <div>
          <h2 className="text-xs uppercase tracking-wide text-neutral-600 mb-1">Context</h2>
          <pre className="text-xs font-mono bg-neutral-50 border border-neutral-200 rounded p-2 overflow-x-auto whitespace-pre-wrap">
            {prettyPrint(safeParse(row.context))}
          </pre>
        </div>
      )}
    </div>
  );
}

// ── Filter inputs ───────────────────────────────────────────────────────

function FilterSelect(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  const id = `flt-${props.label.toLowerCase().replace(/\W+/g, '-')}`;
  return (
    <label htmlFor={id} className="text-xs flex flex-col gap-1">
      <span className="text-neutral-600">{props.label}</span>
      <select
        id={id}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        className="border border-neutral-300 rounded px-2 py-1 bg-white"
      >
        <option value="">All</option>
        {props.options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

function FilterText(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: 'text' | 'date';
}) {
  const id = `flt-${props.label.toLowerCase().replace(/\W+/g, '-')}`;
  return (
    <label htmlFor={id} className="text-xs flex flex-col gap-1">
      <span className="text-neutral-600">{props.label}</span>
      <input
        id={id}
        type={props.type ?? 'text'}
        value={props.value}
        placeholder={props.placeholder}
        onChange={(e) => props.onChange(e.target.value)}
        className="border border-neutral-300 rounded px-2 py-1 bg-white"
      />
    </label>
  );
}

function Padded({ children }: { children: React.ReactNode }) {
  return <div className="p-6 text-sm text-neutral-700">{children}</div>;
}

// ── Plumbing ────────────────────────────────────────────────────────────

const TS = new Intl.DateTimeFormat('en-CA', {
  year: 'numeric', month: 'short', day: 'numeric',
  hour: '2-digit', minute: '2-digit', second: '2-digit',
  hour12: false,
});
function formatTimestamp(ms: number): string {
  return TS.format(new Date(ms));
}

function dateInputToMs(s: string, edge: 'start' | 'end'): number | null {
  if (!s) return null;
  // Local-day boundary so a "From: 2026-05-11" filter catches all logs
  // recorded during that local day. Tests run with a fixed UTC; in practice
  // staff use this filter at human granularity, not millisecond.
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return edge === 'start'
    ? new Date(y, m - 1, d, 0, 0, 0).getTime()
    : new Date(y, m - 1, d + 1, 0, 0, 0).getTime();
}

function safeParse(s: string | null): unknown {
  if (s == null) return null;
  try { return JSON.parse(s); }
  catch { return s; }
}

function prettyPrint(v: unknown): string {
  if (v === null || v === undefined) return '';
  return JSON.stringify(v, null, 2);
}

// Simple key-by-key diff. Both null → '(no payload)'. Pretty-prints JSON
// and marks lines:
//   '+' add (only in after)
//   '-' remove (only in before)
//   '~' change (both, different)
//   ' ' same
type DiffLine = { kind: 'add' | 'remove' | 'change' | 'same'; marker: string; text: string };

function diffJson(before: unknown, after: unknown): DiffLine[] {
  const bLines = prettyPrint(before).split('\n');
  const aLines = prettyPrint(after).split('\n');

  // If the structures match line-count, do a line-aligned diff. Otherwise,
  // show before in red and after in green stacked.
  if (bLines.length === aLines.length) {
    const out: DiffLine[] = [];
    for (let i = 0; i < bLines.length; i++) {
      const same = bLines[i] === aLines[i];
      out.push(
        same
          ? { kind: 'same', marker: ' ', text: aLines[i] }
          : { kind: 'change', marker: '~', text: `${bLines[i]}  →  ${aLines[i]}` },
      );
    }
    return out;
  }
  const out: DiffLine[] = [];
  if (before != null) {
    out.push({ kind: 'remove', marker: '-', text: '-- before --' });
    for (const l of bLines) out.push({ kind: 'remove', marker: '-', text: l });
  }
  if (after != null) {
    out.push({ kind: 'add', marker: '+', text: '-- after --' });
    for (const l of aLines) out.push({ kind: 'add', marker: '+', text: l });
  }
  return out;
}
